import React, { useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { create } from "../../api/API";
import { getCurrentUser, isLoggedIn } from "../../utility/UserUtils";
import { toast } from "react-toastify";
import LoadingSpinner from "../Generic/LoadingSpinner";
import Seo from "../Generic/Seo";
import GoogleAnalytics from "../Generic/GoogleAnalytics";

const GiveReferral = () => {
  const [formData, setFormData] = useState({
    company: "",
    role: "",
    location: "",
    experience: "",
    description: "",
    jobLink: "",
    expiryDate: "",
  });
  const [loading, setLoading] = useState(false);

  const seoData = {
    title: "Offer a Referral — WiSE Referral Program",
    description:
      "Post job openings where you can offer referrals. Help fellow women in semiconductors find opportunities and grow their careers.",
    keywords:
      "give referral, post referral job, WiSE referral program, women in semiconductor referrals, job referral listing",
    pathSlug: "give-referral",
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const renderExpiryInfo = () => {
    if (!formData.expiryDate) return null;

    const expiry = new Date(formData.expiryDate);
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 0) {
      return (
        <Form.Text className="text-success fw-semibold">
          Expires in {diffDays} day{diffDays !== 1 ? "s" : ""} (
          {expiry.toLocaleDateString()})
        </Form.Text>
      );
    } else if (diffDays === 0) {
      return (
        <Form.Text className="text-warning fw-semibold">
          This post expires today ({expiry.toLocaleDateString()}).
        </Form.Text>
      );
    } else {
      return (
        <Form.Text className="text-danger fw-semibold">
          The selected date is in the past ({expiry.toLocaleDateString()}).
        </Form.Text>
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isLoggedIn()) {
      toast.info("Please login to post a referral opportunity.");
      return;
    }

    const { company, role, location, experience } = formData;
    if (!company || !role || !location || !experience) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const currentUser = getCurrentUser();
    if (!currentUser) {
      toast.error("User session expired. Please login again.");
      return;
    }

    setLoading(true);
    try {
      await create("referral-jobs", {
        postedBy: currentUser.id,
        ...formData,
        expiryDate: formData.expiryDate || undefined,
      });

      toast.success("Referral submitted successfully!");
      setFormData({
        company: "",
        role: "",
        location: "",
        experience: "",
        description: "",
        jobLink: "",
        expiryDate: "",
      });
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      company: "",
      role: "",
      location: "",
      experience: "",
      description: "",
      jobLink: "",
      expiryDate: "",
    });
  };

  return (
    <>
      <Seo data={seoData} />
      <GoogleAnalytics page={seoData.pathSlug} title={seoData.title} />

      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100vh",
            backgroundColor: "rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <LoadingSpinner text="Posting your referral..." />
        </div>
      )}

      <Container className="my-5">
        <Row className="justify-content-center">
          <Col md={12}>
            <div className="text-left mb-4">
              <h6 className="text-muted small">
                Verified community referrals only — respectful, private, and
                trackable
              </h6>
            </div>

            <div className="referral-form shadow-sm rounded p-4 bg-white">
              <h4 className="fw-bold mb-4 text-left">I can give referral</h4>

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Name*</Form.Label>
                  <Form.Control
                    type="text"
                    name="company"
                    placeholder="e.g. WiSE"
                    value={formData.company}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Role / Position*</Form.Label>
                  <Form.Control
                    type="text"
                    name="role"
                    placeholder="e.g. VLSI Engineer"
                    value={formData.role}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Location*</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    placeholder="e.g. Bangalore / Remote"
                    value={formData.location}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Experience*</Form.Label>
                  <Form.Control
                    type="text"
                    name="experience"
                    placeholder="e.g. 3–5 years"
                    value={formData.experience}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    placeholder="Briefly describe the referral opportunity"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Link for Job Profile*</Form.Label>
                  <Form.Control
                    type="url"
                    name="jobLink"
                    placeholder="Paste job link here"
                    value={formData.jobLink}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Expiry Date (Optional)</Form.Label>
                  <Form.Control
                    type="date"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleChange}
                    min={new Date().toISOString().split("T")[0]}
                  />
                  {renderExpiryInfo()}
                  <Form.Text className="text-muted">
                    If not set, this post will expire automatically after 30
                    days.
                  </Form.Text>
                </Form.Group>

                <div className="d-flex flex-row flex-wrap justify-content-start gap-2 mt-3">
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    className="px-4 py-2 flex-fill flex-sm-grow-0"
                  >
                    Submit
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="px-4 py-2 flex-fill flex-sm-grow-0"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default GiveReferral;
